import { useState, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { useLanguage } from '@/context/LanguageContext';
import { ZoomIn } from 'lucide-react';

const galleryItems = [
  { id: 1, title: 'Engraved Slate Coaster', material: 'slate', img: 'https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?w=500&h=600&fit=crop', desc: 'Hand-finished natural slate with precision laser detail.' },
  { id: 2, title: 'Walnut Wine Box', material: 'wood', img: 'https://images.unsplash.com/photo-1600181897467-dcdadfe88cd4?w=500&h=600&fit=crop', desc: 'American walnut, laser-engraved with custom typography.' },
  { id: 3, title: 'Brushed Steel Tag', material: 'metal', img: 'https://images.unsplash.com/photo-1558618666-fcd25c85f82e?w=500&h=600&fit=crop', desc: 'Surgical-grade stainless steel with micron-level engraving.' },
  { id: 4, title: 'Full-Grain Journal', material: 'leather', img: 'https://images.unsplash.com/photo-1544816155-12df9643f363?w=500&h=600&fit=crop', desc: 'Italian full-grain leather, debossed with laser precision.' },
  { id: 5, title: 'Slate Wall Art', material: 'slate', img: 'https://images.unsplash.com/photo-1513364776144-60967b0f800f?w=500&h=600&fit=crop', desc: 'Large-format slate panel with intricate geometric patterns.' },
  { id: 6, title: 'Oak Cutting Board', material: 'wood', img: 'https://images.unsplash.com/photo-1606760227091-3dd870d97f1d?w=500&h=600&fit=crop', desc: 'White oak cutting board with deep-etched family crest.' },
  { id: 7, title: 'Titanium Bracelet', material: 'metal', img: 'https://images.unsplash.com/photo-1573408301185-9146fe634ad0?w=500&h=600&fit=crop', desc: 'Grade 5 titanium bracelet with personalized coordinates.' },
  { id: 8, title: 'Leather Card Holder', material: 'leather', img: 'https://images.unsplash.com/photo-1548036328-c9fa89d128fa?w=500&h=600&fit=crop', desc: 'Vegetable-tanned leather with NFC chip embedded.' },
];

const filters = ['all', 'slate', 'wood', 'metal', 'leather'];

export default function StudioPage() {
  const { t } = useLanguage();
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedItem, setSelectedItem] = useState(null);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });

  const filtered = activeFilter === 'all' ? galleryItems : galleryItems.filter((i) => i.material === activeFilter);

  return (
    <div className="pt-24 pb-20">
      <section ref={ref} className="max-w-7xl mx-auto px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={isInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.6 }} className="mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold text-title mb-4">{t('studio.title')}</h1>
          <p className="text-base text-body max-w-lg">{t('studio.subtitle')}</p>
        </motion.div>

        <div className="flex flex-wrap gap-3 mb-10">
          {filters.map((f) => (
            <Button
              key={f}
              variant={activeFilter === f ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveFilter(f)}
              className={`text-xs tracking-wider capitalize font-medium transition-all duration-300 ${
                activeFilter === f 
                  ? 'laser-engraved metallic-bg sheen-effect border-silver' 
                  : 'border-border text-body hover:text-title hover:bg-secondary hover:border-silver/50'
              }`}
            >
              {t(`studio.${f}`)}
            </Button>
          ))}
        </div>

        <motion.div layout className="columns-1 sm:columns-2 lg:columns-3 gap-5 space-y-5">
          <AnimatePresence mode="popLayout">
            {filtered.map((item, i) => (
              <motion.div key={item.id} layout initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.9 }} transition={{ duration: 0.4, delay: i * 0.05 }} className="break-inside-avoid">
                <div className="group relative rounded-xl overflow-hidden border border-border bg-card cursor-pointer" onClick={() => setSelectedItem(item)}>
                  <div className="relative overflow-hidden">
                    <img src={item.img} alt={item.title} className="w-full object-cover group-hover:scale-105" style={{ transition: 'transform 0.5s ease' }} />
                    <div className="absolute inset-0 bg-background/60 opacity-0 group-hover:opacity-100 flex items-center justify-center pointer-events-none" style={{ transition: 'opacity 0.3s ease' }}>
                      <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center border border-primary/30">
                        <ZoomIn className="w-4 h-4 text-primary" />
                      </div>
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-sm font-semibold text-title">{item.title}</h3>
                      <Badge variant="outline" className="text-[10px] capitalize border-border text-body">{item.material}</Badge>
                    </div>
                    <p className="text-xs text-body line-clamp-2">{item.desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </section>

      <Dialog open={!!selectedItem} onOpenChange={() => setSelectedItem(null)}>
        <DialogContent className="max-w-2xl bg-card border-border p-0 overflow-hidden">
          <DialogTitle className="sr-only">{selectedItem?.title}</DialogTitle>
          {selectedItem && (
            <div>
              <div className="relative">
                <img src={selectedItem.img} alt={selectedItem.title} className="w-full max-h-[60vh] object-cover" />
              </div>
              <div className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xl font-semibold text-title">{selectedItem.title}</h3>
                  <Badge variant="outline" className="capitalize border-primary/30 text-primary text-xs">{selectedItem.material}</Badge>
                </div>
                <p className="text-sm text-body">{selectedItem.desc}</p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
